import React, { Fragment } from "react";
import classnames from "classnames";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Container,
  Col,
  Label,
  FormGroup,
  InputGroup,
  Input,
  Row,
  CardImg,
  CardTitle,
} from "reactstrap";
import Select from "react-select";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SimpleHeader from "@/components/Headers/SimpleHeader";
import ProductTable from "./ProductTable";
import NumericPropertyInput, { isNumericPropertyType } from "./NumericPropertyInput";

/** A product row of the Set Initial Properties table. */
export type PropertiesProductRow = {
  _id: string;
  title: string;
  productImg?: string;
};

/** A property offered for the chosen subcategory, with the value the editor typed. */
export type InitialPropertyRow = {
  _id: string;
  name: string;
  type: string;
  value?: string;
};

export type CategoryOption = {
  value: { name?: string; _id: string };
  label?: string;
};

export type SubcategoryOption = {
  value: { name?: string; _id: string; category: string };
  label?: string;
};

type AddProductPropertiesViewProps = {
  canUse: boolean;
  products: PropertiesProductRow[];
  mappedCategories: CategoryOption[];
  mappedSubcategories: SubcategoryOption[];
  selectedRow: PropertiesProductRow | null;
  showPanel: boolean;
  showSubcategories: boolean;
  showProperties: boolean;
  selectedProperties: InitialPropertyRow[];
  onShowAdd: (event: unknown, row: PropertiesProductRow) => void;
  onClosePanel: () => void;
  onCategoryChange: (option: CategoryOption | null) => void;
  onSubcategoryChange: (option: SubcategoryOption | null) => void;
  onPropertyChange: (name: string, value: string) => void;
  onAdd: () => void;
};

const AddProductPropertiesView = ({
  // perms
  canUse,
  // data
  products,
  mappedCategories,
  mappedSubcategories,
  selectedRow,
  showPanel,
  showSubcategories,
  showProperties,
  selectedProperties,
  // handlers
  onShowAdd,
  onClosePanel,
  onCategoryChange,
  onSubcategoryChange,
  onPropertyChange,
  onAdd,
}: AddProductPropertiesViewProps) => {
  return (
    <>
      <ToastContainer />
      <SimpleHeader name="Set Initial Properties" parentName="Products" />
      <Container className="mt--6" fluid>
        <Row>
          <Col>
            <Card>
              <CardHeader>
                <h5 className="h3 mb-0">Add Properties</h5>
                <p className="text-muted mt-2">
                  Filtered by the current site country. Click a product to start.
                </p>
              </CardHeader>
              <CardBody>
                <ProductTable data={products} onAddClick={onShowAdd} />
              </CardBody>
            </Card>
          </Col>

          {canUse && showPanel && selectedRow && (
            <Col md="5">
              <Card>
                <CardImg alt="Product" src={selectedRow.productImg || ""} top />
                <CardBody>
                  <CardTitle className="h2 mb-0">{selectedRow.title}</CardTitle>

                  {/* Category */}
                  <FormGroup className="row mt-4">
                    <Label className="form-control-label ml-2">Category</Label>
                    <Col>
                      <Select
                        name="category"
                        options={mappedCategories}
                        onChange={onCategoryChange}
                        isClearable
                        isSearchable
                      />
                    </Col>
                  </FormGroup>

                  {/* Subcategory — trunks only (SCRUM-1718); an empty list is named, not an empty select */}
                  {showSubcategories && mappedSubcategories.length === 0 && (
                    <FormGroup className="row">
                      <Label className="form-control-label ml-2">Subcategory</Label>
                      <Col>
                        <p className="text-muted mb-0" data-testid="no-trunk-subcategory">
                          No subcategory in this category hosts products
                        </p>
                      </Col>
                    </FormGroup>
                  )}
                  {showSubcategories && mappedSubcategories.length > 0 && (
                    <FormGroup className="row">
                      <Label className="form-control-label ml-2">Subcategory</Label>
                      <Col>
                        <Select
                          name="subcategory"
                          options={mappedSubcategories}
                          onChange={onSubcategoryChange}
                          isClearable
                          isSearchable
                        />
                      </Col>
                    </FormGroup>
                  )}

                  {/* Properties */}
                  {showProperties && selectedProperties.length > 0 && (
                    <>
                      <h6 className="heading-small text-muted mb-4">
                        Properties List
                      </h6>
                      {selectedProperties.map((property) => (
                        <Fragment key={property.name}>
                          <FormGroup className="row justify-content-between">
                            <Col md={4}>
                              <Label className="form-control-label mt-2">
                                {property.name}
                                <small className="form-text text-muted d-block">
                                  {property.type}
                                </small>
                              </Label>
                            </Col>
                            <Col md={8}>
                              <InputGroup className={classnames("input-group-merge")}>
                                {isNumericPropertyType(property.type) ? (
                                  <NumericPropertyInput
                                    name={property.name}
                                    value={property.value || ""}
                                    onChange={onPropertyChange}
                                  />
                                ) : (
                                  <Input
                                    type="text"
                                    placeholder={property.name}
                                    name={property.name}
                                    value={property.value || ""}
                                    onChange={(e) =>
                                      onPropertyChange(property.name, e.target.value)
                                    }
                                  />
                                )}
                              </InputGroup>
                            </Col>
                          </FormGroup>
                        </Fragment>
                      ))}
                    </>
                  )}

                  <Row className="mt-4">
                    <Col>
                      <Button color="primary" onClick={onAdd}>
                        Add
                      </Button>
                      <Button className="ml-2" color="danger" onClick={onClosePanel}>
                        Close
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          )}
        </Row>
      </Container>
    </>
  );
};

export default AddProductPropertiesView;
